import { Link } from 'react-router-dom';
import { ArrowRight, Activity, Cpu, TrendingUp, User, Truck, Building2, Sliders } from 'lucide-react';
import { motion } from 'framer-motion';
import { containerVariants, fadeUpVariants } from '../lib/motion';

export default function HostPage() {
  const steps = [
    { num: '01', title: 'Bring the Asset', desc: 'Add eligible electric vehicles to the E Drives platform.' },
    { num: '02', title: 'We Deploy & Operate', desc: 'E Drives puts the vehicle into active mobility operations and manages day-to-day deployment.' },
    { num: '03', title: 'Share the Opportunity', desc: "Participate in revenue generated through the vehicle's operations." }
  ];

  const valueCards = [
    { icon: Activity, title: 'Real Mobility Demand', desc: 'Vehicles are deployed across active mobility operations.' },
    { icon: Sliders, title: 'Managed Operations', desc: 'We manage deployment, riders and day-to-day operations.' },
    { icon: Cpu, title: 'Connected Technology', desc: 'Technology brings visibility into asset and operational performance.' },
    { icon: TrendingUp, title: 'Growing Mobility Network', desc: 'A platform expanding across multiple mobility verticals.' }
  ];

  const partnerTypes = [
    { icon: User, title: 'Individuals', desc: 'Own EV assets and bring them onto the E Drives platform.' },
    { icon: Truck, title: 'Fleet Owners', desc: 'Connect EV fleets with active mobility operations.' },
    { icon: Building2, title: 'Leasing Partners', desc: 'Deploy leased assets through the E Drives mobility network.' }
  ];

  return (
    <>
      {/* Section 1: Hero */}
      <section className="pt-36 pb-16 bg-brand-mint relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50" />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-brand-lime/25 rounded-full blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 text-left"
          >
            <span className="text-xs font-bold text-brand-green uppercase tracking-wider bg-white/80 border border-brand-dark/10 px-3 py-1 rounded-full">
              Asset Partnership
            </span>
            <h1 className="heading-display text-4xl sm:text-5xl md:text-6xl text-brand-dark mt-4 font-bold leading-[1.1]">
              Your EV. Our Platform. <span className="italic font-light">Shared Opportunity.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-brand-dark/75 max-w-xl leading-relaxed">
              Bring electric vehicles onto the E Drives platform and participate in revenue generated through active mobility operations.
            </p>
            
            <div className="mt-8">
              <Link to="/contact?type=host" className="btn-dark-pill">
                Become an Asset Partner <ArrowRight size={18} />
              </Link>
            </div>
            
            <p className="mt-4 text-xs font-semibold text-brand-green/80 uppercase tracking-wider">
              For Individuals · Fleet Owners · Leasing Partners
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-brand-dark/10 group">
              <img 
                src="/host_handshake.jpg" 
                alt="E Drives Asset Partnership" 
                className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-500 rounded-3xl" 
              />
            </div>
          </motion.div>

        </div>
      </section>

      {/* Section 2: How It Works */}
      <section className="section-pad bg-white border-b border-brand-dark/5">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <div className="max-w-2xl mx-auto mb-16">
            <h2 className="heading-display text-3xl md:text-5xl text-brand-dark font-bold">Your Asset. Our Mobility Network.</h2>
            <p className="mt-4 text-brand-dark/65 text-base md:text-lg">A simple way to put EV assets into active mobility operations.</p>
          </div>

          <motion.div 
            variants={containerVariants(0.15)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
          >
            {steps.map((s, idx) => (
              <motion.div 
                key={s.num} 
                variants={fadeUpVariants(25, 0.6)}
                className="bg-brand-lavenderSoft rounded-3xl p-8 border border-brand-dark/5 text-left relative hover:-translate-y-1 hover:shadow-md transition-all duration-300"
              >
                <span className="text-4xl font-extrabold text-brand-green/20 block mb-4">{s.num}</span>
                <h3 className="heading-display text-xl text-brand-dark font-bold mb-2">{s.title}</h3>
                <p className="text-sm text-brand-dark/70 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Horizontal Visual Flow Diagram */}
          <div className="inline-flex flex-wrap items-center justify-center gap-4 bg-brand-lavenderSoft border border-brand-dark/5 px-6 py-4 rounded-2xl shadow-sm text-xs md:text-sm font-semibold text-brand-dark">
            <span className="bg-brand-mint text-brand-dark border border-brand-dark/10 px-3 py-1.5 rounded-xl uppercase tracking-wider shadow-sm text-[10px] md:text-xs">Asset</span>
            <span className="text-brand-green font-bold">➔</span>
            <span className="bg-brand-lime text-brand-dark border border-brand-dark/10 px-3 py-1.5 rounded-xl uppercase tracking-wider shadow-sm text-[10px] md:text-xs">E Drives Platform</span>
            <span className="text-brand-green font-bold">➔</span>
            <span className="bg-brand-mint text-brand-dark border border-brand-dark/10 px-3 py-1.5 rounded-xl uppercase tracking-wider shadow-sm text-[10px] md:text-xs">Mobility Operations</span>
          </div>
        </div>
      </section>

      {/* Section 3: Why E Drives */}
      <section className="section-pad bg-brand-lavender">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="heading-display text-3xl md:text-5xl text-brand-dark font-bold">Built to Keep Assets Moving.</h2>
          </div>

          <motion.div 
            variants={containerVariants(0.1)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {valueCards.map((c) => {
              const Icon = c.icon;
              return (
                <motion.div key={c.title} variants={fadeUpVariants(20, 0.5)}>
                  <div className="bg-white rounded-3xl p-6 border border-brand-dark/5 shadow-sm hover:shadow-lg hover:-translate-y-1.5 transition-all duration-300 h-full">
                    <div className="w-10 h-10 rounded-xl bg-brand-mint flex items-center justify-center text-brand-dark mb-4 shadow-sm">
                      <Icon size={18} />
                    </div>
                    <h3 className="heading-display text-lg text-brand-dark font-bold mb-2">{c.title}</h3>
                    <p className="text-sm text-brand-dark/65 leading-relaxed">{c.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Section 4: Who Can Partner */}
      <section className="section-pad bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="heading-display text-3xl md:text-5xl text-brand-dark font-bold">Built for Every Asset Partner.</h2>
          </div>

          <motion.div 
            variants={containerVariants(0.12)}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
          >
            {partnerTypes.map((p) => {
              const Icon = p.icon;
              return (
                <motion.div key={p.title} variants={fadeUpVariants(25, 0.6)}>
                  <div className="bg-brand-lavenderSoft rounded-3xl p-8 border border-brand-dark/5 text-center shadow-sm hover:shadow-md transition-shadow h-full flex flex-col items-center">
                    <div className="w-14 h-14 rounded-2xl bg-brand-mint text-brand-dark flex items-center justify-center mb-5 shadow-sm">
                      <Icon size={24} />
                    </div>
                    <h3 className="heading-display text-xl text-brand-dark font-bold mb-2">{p.title}</h3>
                    <p className="text-sm text-brand-dark/70 leading-relaxed max-w-xs">{p.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Section 5: Final CTA */}
      <section className="py-20 bg-brand-mint relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative max-w-4xl mx-auto px-6 text-center"
        >
          <h2 className="heading-display text-3xl md:text-5xl text-brand-dark font-bold">Put Your EV Assets in Motion.</h2>
          <p className="text-brand-dark/75 mt-4 text-base md:text-lg max-w-2xl mx-auto">
            Join the E Drives asset network and participate in a growing mobility ecosystem.
          </p>
          <div className="mt-8">
            <Link to="/contact?type=host" className="btn-dark-pill">
              Become an Asset Partner <ArrowRight size={18} />
            </Link>
          </div>
        </motion.div>
      </section>
    </>
  );
}
